import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense } from 'react';

import {
  DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS,
  AboutUsDocument,
  AboutUs,
} from '@xing-com/crate-entity-pages-about-us';
import {
  ModuleHeader,
  MetaData,
  WarningMessageBox as Error,
} from '@xing-com/crate-entity-pages-common';
import { getTrackingUrl } from '@xing-com/crate-entity-pages-common/src/tracking';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

const AboutUsModuleInner: FC = () => {
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();

  const { data, refetch } = useSuspenseQuery(AboutUsDocument, {
    variables: {
      id: pageContext.pageSlug,
      first: DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS,
    },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const aboutEntityPage =
    data?.pagesAboutUs?.__typename === 'AboutEntity' ? data.pagesAboutUs : null;

  const upsellActive = pageContext.upsellActive;

  const hasError = !aboutEntityPage?.summary;

  const hasSubpage = aboutEntityPage?.properties?.hasSubpage;

  const entityPageMediaEdges =
    aboutEntityPage?.media?.__typename === 'EntityMediaConnection'
      ? (aboutEntityPage?.media?.edges ?? [])
      : [];

  const entityPageMedia =
    entityPageMediaEdges?.map((edge) => edge?.node).filter((node) => !!node) ??
    [];

  const showMore =
    hasSubpage && !hasError
      ? {
          to: getTrackingUrl({
            to: `${pageContext.basePath}/${pageContext.pageSlug}/about_us`,
            trackingKey: 'learnMoreLink',
          }),
          copyKey: 'EP_ABOUT_US_DISCOVER_CTA',
          testId: 'moreAboutUsLink',
        }
      : null;

  return (
    <>
      <ModuleHeader
        headlineCopyKey="EP_ABOUT_US_HEADLINE"
        showMore={showMore}
      />

      {hasError ? (
        <div data-testid={'errorContainer'}>
          <Error
            headerText="EP_ERROR_HEADER"
            bodyText="EP_ERROR_BODY"
            buttonText="EP_ERROR_RELOAD_CTA"
            onClick={() => refetch()}
          />
        </div>
      ) : null}

      {data && !hasError ? (
        <>
          <MetaData description={aboutEntityPage?.summary} />
          <AboutUs
            pageSlug={pageContext.pageSlug}
            upsellActive={upsellActive}
            // @ts-expect-error TODO: fix this type
            headline={aboutEntityPage?.headline}
            // @ts-expect-error TODO: fix this type
            summary={aboutEntityPage?.summary}
            galleryItems={entityPageMedia}
            badges={[]}
            hasSubpage={!!hasSubpage}
            isEditing={!!isEditing}
          />
        </>
      ) : null}
    </>
  );
};

const Fallback: FC = () => {
  return (
    <>
      <ModuleHeader
        headlineCopyKey="EP_ABOUT_US_HEADLINE"
        showMore={{ isLoading: true }}
      />
      <AboutUs.Skeleton />
    </>
  );
};

export const AboutUsModule: FC = () => {
  return (
    <Suspense fallback={<Fallback />}>
      <AboutUsModuleInner />
    </Suspense>
  );
};
